<template>
  <div class="login-shell">
    <section class="login-introduction">
      <button type="button" class="login-brand" @click="home">
        <span class="login-brand__mark"><v-icon color="white">school</v-icon></span>
        <span class="login-brand__name">taleem<span>.app</span></span>
      </button>
      <div class="login-introduction__content">
        <div class="login-eyebrow">Masjid administration</div>
        <h1>Welcome back to Taleem.</h1>
        <p>Choose a new password to continue managing your learning community.</p>
        <div class="login-feature">
          <v-icon>check_circle</v-icon>
          <span>Simple, secure, and built for your community</span>
        </div>
      </div>
    </section>

    <v-card class="login-card" elevation="0">
      <div class="login-card__heading">
        <div class="login-card__icon"><v-icon>lock_reset</v-icon></div>
        <div>
          <div class="login-eyebrow">Account recovery</div>
          <h2>Set a new password</h2>
          <p>Use at least eight characters for your new password.</p>
        </div>
      </div>

      <v-card-text class="login-card__body">
        <form v-if="token && !complete" class="auth-recovery-form" novalidate @submit.prevent="submit">
          <v-text-field
            v-model="password"
            :error-messages="passwordErrors"
            :hide-details="passwordErrors.length === 0"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'visibility_off' : 'visibility'"
            label="New password"
            prepend-inner-icon="lock"
            autocomplete="new-password"
            outlined
            class="login-field"
            @click:append="showPassword = !showPassword"
            @blur="$v.password.$touch()"
          />
          <v-text-field
            v-model="confirmation"
            :error-messages="confirmationErrors"
            :hide-details="confirmationErrors.length === 0"
            :type="showPassword ? 'text' : 'password'"
            label="Confirm new password"
            prepend-inner-icon="lock"
            autocomplete="new-password"
            outlined
            class="login-field"
            @blur="$v.confirmation.$touch()"
          />
          <v-btn :loading="loading" class="login-submit" color="primary" depressed block large type="submit">
            Reset password <v-icon right>arrow_forward</v-icon>
          </v-btn>
        </form>
        <p v-if="!token" class="auth-status auth-status--error" role="alert">This reset link is missing or invalid. Request a new one.</p>
        <p v-if="requestError" class="auth-status auth-status--error" role="alert">{{ requestError }}</p>
        <p v-if="complete" class="auth-status" role="status">Your password has been updated. You can now sign in.</p>
      </v-card-text>

      <div class="login-card__signup">
        <button type="button" @click="$router.push(complete ? '/login' : '/forgot')">
          {{ complete ? 'Go to sign in' : 'Request a new link' }}
        </button>
      </div>
    </v-card>
  </div>
</template>

<script>
import authService from '../service'
const { validationMixin } = require('vuelidate')
const { required, minLength, sameAs } = require('vuelidate/lib/validators')

export default {
  name: 'authentication-reset-password',
  mixins: [validationMixin],
  data() {
    return { password: '', confirmation: '', loading: false, complete: false, requestError: '', showPassword: false }
  },
  validations: {
    password: { required, minLength: minLength(8) },
    confirmation: { required, sameAsPassword: sameAs('password') },
  },
  computed: {
    token() { return String(this.$route.query.token || '') },
    passwordErrors() {
      const errors = []
      if (!this.$v.password.$dirty) return errors
      if (!this.$v.password.required) errors.push('Password is required.')
      else if (!this.$v.password.minLength) errors.push('Use at least eight characters.')
      return errors
    },
    confirmationErrors() {
      const errors = []
      if (!this.$v.confirmation.$dirty) return errors
      if (!this.$v.confirmation.required) errors.push('Confirm your password.')
      else if (!this.$v.confirmation.sameAsPassword) errors.push('Passwords do not match.')
      return errors
    },
  },
  methods: {
    home() { window.open('https://taleem.com', '_self') },
    async submit() {
      this.$v.$touch()
      if (this.$v.$invalid || !this.token || this.loading) return
      this.loading = true
      this.requestError = ''
      try {
        await authService.resetPassword({ token: this.token, newPassword: this.password })
        this.complete = true
        this.password = ''
        this.confirmation = ''
      } catch (error) {
        this.requestError = error && error.status === 401
          ? 'This link has expired or has already been used. Request a new one.'
          : (error && error.data && error.data.message) || 'Unable to reset your password. Please try again.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
